import { defineStore } from "pinia";
import { useRest } from "@/composables/useRest";
import { ref, watch } from "vue";

export const useEstadisticasStore = defineStore("estadísticas", () => {
  const rest = useRest();
  const loading = ref(false);
  const dependenciaSelected = ref(null);
  const baseUrl = process.env.VUE_APP_BACKEND_ENVIRONMENT;
  const vencidas = ref([]);
  const enCurso = ref([]);
  const finalizadas = ref([]);

  const totalActuations = ref({});
  const UFIStats = ref([]);
  const gralStats = ref({
    ufis: {
      label: "UFI",
      enCurso: 0,
      vencidas: 0,
      finalizadas: 0,
    },
    ufisContraPropiedad: {
      label: "UFI - Contra la propiedad",
      enCurso: 0,
      vencidas: 0,
      finalizadas: 0,
      titleSize: "16px",
    },
    sumarios: {
      label: "Sumarios",
      enCurso: 0,
      vencidas: 0,
      finalizadas: 0,
    },
    expedientes: {
      label: "Expedientes",
      enCurso: 0,
      vencidas: 0,
      finalizadas: 0,
    },
  });

  const actuacionesUFI = {
    "unidades fiscales de investigacion flagrancia": "Flagrancia",
    "unidades fiscales de investigacion anivi": "Anivi",
    "unidades fiscales de investigacion cavig": "Cavig",
    "unidades fiscales de investigacion d.e ahorcamiento o suicidio": "Ahorcamiento o Suicidio",
    "unidades fiscales de investigacion d.e emergencias medicas": "Emergencias medicas",
    "unidades fiscales de investigacion d.e establecer paradero": "Establecer paradero",
    "unidades fiscales de investigacion d.e incendio en vivienda": "Incendio en vivienda",
    "unidades fiscales de investigacion d.e intoxicacion": "Intoxicación",
    "unidades fiscales de investigacion d.e siniestro vial": "Siniestro vial",
    "unidades fiscales de investigacion d.e otros": "Otros",
    "unidades fiscales de investigación delitos contra la propiedad por denuncia":
      "Contra la propiedad por denuncia",
    "unidades fiscales de investigación delitos contra la propiedad por oficio":
      "Contra la propiedad por oficio",
    "unidades fiscales de investigación delitos genéricos por denuncia": "Genéricos por denuncia",
    "unidades fiscales de investigación delitos genéricos por oficio": "Genéricos por oficio",
    "unidades fiscales de investigación delitos informáticos y estafas por denuncia":
      "Informáticos y estafas por denuncia",
    "unidades fiscales de investigación delitos informáticos y estafas por oficio":
      "Informáticos y estafas por oficio",
  };

  const ufiContraPropiedad = [
    "unidades fiscales de investigación delitos contra la propiedad por denuncia",
    "unidades fiscales de investigación delitos contra la propiedad por oficio",
  ];

  const sumarios = ["sumario prevencional por denuncia", "sumario prevencional por oficio"];

  const ordenUFIs = [
    "Flagrancia",
    "Contra la propiedad por denuncia",
    "Contra la propiedad por oficio",
    "Informáticos y estafas por denuncia",
    "Informáticos y estafas por oficio",
    "Cavig",
    "Anivi",
    "Genéricos por denuncia",
    "Genéricos por oficio",
    "Establecer paradero",
    "Emergencias medicas",
    "Siniestro vial",
    "Ahorcamiento o Suicidio",
    "Incendio en vivienda",
    "Intoxicación",
    "Otros",
  ];

  const buildSearch = (estado, tipo) => {
    const base = search.value[estado] || {};
    const filtro = JSON.parse(JSON.stringify(base));

    filtro["exp_pre_Actuacion.TiposumarioID"] = [{ operator: "=", value: tipo }];
    return `search=${encodeURIComponent(JSON.stringify(filtro))}`;
  };

  const search = ref({
    enCurso: {
      "exp_pre_EstadoActuacion.TipoEstadoActuacionID": [{ operator: "=", value: "En Curso" }],
    },
    vencida: {
      "exp_pre_EstadoActuacion.TipoEstadoActuacionID": [{ operator: "=", value: "Vencida" }],
    },
    finalizadas: {
      "exp_pre_EstadoActuacion.TipoEstadoActuacionID": [{ operator: "=", value: "Finalizada" }],
    },
  });

  const getData = async (urls) => {
    let respCurso = await rest.get(urls[0], { withCredentials: true });
    enCurso.value = respCurso.data.data;
    totalActuations.value.enCurso = respCurso.data.total;
    let respVencidas = await rest.get(urls[1], { withCredentials: true });
    vencidas.value = respVencidas.data.data;
    totalActuations.value.vencidas = respVencidas.data.total;
  };

  const categorizeGeneralStats = () => {
    Object.keys(gralStats.value).forEach((key) => {
      gralStats.value[key].enCurso = 0;
      gralStats.value[key].vencidas = 0;
      gralStats.value[key].finalizadas = 0;
    });

    const acumular = (arr, estado) => {
      arr.forEach((item) => {
        const tipo = item.TiposumarioID?.toLowerCase().trim();
        if (!tipo) return;

        if (tipo.includes("unidades fiscales de")) {
          gralStats.value.ufis[estado] += 1;
        }

        if (ufiContraPropiedad.includes(tipo)) {
          gralStats.value.ufisContraPropiedad[estado] += 1;
        }

        if (sumarios.includes(tipo)) {
          gralStats.value.sumarios[estado] += 1;
        }

        if (tipo.includes("expediente")) {
          gralStats.value.expedientes[estado] += 1;
        }
      });
    };

    acumular(enCurso.value || [], "enCurso");
    acumular(vencidas.value || [], "vencidas");
  };

  const categorizarTipoUfis = () => {
    const resultado = {};
    const acumular = (arr, estado) => {
      arr.forEach((item) => {
        const tipoUfi = item.TiposumarioID.toLowerCase().trim();
        const tipo = actuacionesUFI[tipoUfi];

        if (!tipo) return;

        if (!resultado[tipo]) {
          resultado[tipo] = {
            tipoSumarioID: tipo,
            label: tipo,
            enCurso: 0,
            vencidas: 0,
            total: 0,
          };
        }

        resultado[tipo][estado] += 1;
        resultado[tipo].total += 1;
      });
    };

    acumular(enCurso.value || [], "enCurso");
    acumular(vencidas.value || [], "vencidas");
    acumular(finalizadas.value || [], "finalizadas");

    return Object.values(resultado).sort((a, b) => {
      return ordenUFIs.indexOf(a.label) - ordenUFIs.indexOf(b.label);
    });
  };

  const fetchEstadisticas = async () => {
    loading.value = true;

    const tipos = ["ufi", "sumario", "expediente"];
    const estados = ["enCurso", "vencida", "finalizadas"];

    const requests = [];
    enCurso.value = [];
    vencidas.value = [];
    finalizadas.value = [];
    UFIStats.value = [];

    Object.keys(gralStats.value).forEach((key) => {
      gralStats.value[key].enCurso = 0;
      gralStats.value[key].vencidas = 0;
      gralStats.value[key].finalizadas = 0;
    });

    for (const estado of estados) {
      for (const tipo of tipos) {
        const url = `${baseUrl}/Actuacion/estadisticas?${buildSearch(estado, tipo)}`;
        requests.push(rest.get(url, { withCredentials: true }));
      }
    }
    try {
      const responses = await Promise.all(requests);
      responses.forEach((resp, indice) => {
        let estado;
        if (indice < 3) estado = "enCurso";
        else if (indice < 6) estado = "vencidas";
        else estado = "finalizadas";
        const tipo = tipos[indice % 3];

        if (!resp.data || typeof resp.data.total !== "number") return;

        switch (tipo) {
          case "ufi":
            gralStats.value.ufis[estado] += resp.data.total;
            if (resp.data.data?.length) {
              for (const act of resp.data.data) {
                const tipoLower = act.TiposumarioID?.toLowerCase().trim();
                if (ufiContraPropiedad.includes(tipoLower)) {
                  gralStats.value.ufisContraPropiedad[estado] += 1;
                }
              }
            }
            if (estado === "enCurso") {
              enCurso.value.push(...resp.data.data);
            } else if (estado === "vencidas") {
              vencidas.value.push(...resp.data.data);
            } else if (estado === "finalizadas") {
              finalizadas.value.push(...resp.data.data);
            }
            break;

          case "sumario":
            gralStats.value.sumarios[estado] += resp.data.total;
            break;

          case "expediente":
            gralStats.value.expedientes[estado] += resp.data.total;
            break;
        }
      });

      UFIStats.value = categorizarTipoUfis();
    } catch (e) {
      console.error("Error al obtener Estadísticas:", e);
    } finally {
      loading.value = false;
    }
  };

  watch(dependenciaSelected, (newVal) => {
    if (!newVal) {
      delete search.value.enCurso["exp_pre_ActuacionUsuarioDependencia.DependenciaID"];
      delete search.value.vencida["exp_pre_ActuacionUsuarioDependencia.DependenciaID"];
      fetchEstadisticas();
      return;
    }
    search.value.enCurso["exp_pre_ActuacionUsuarioDependencia.DependenciaID"] = [
      { operator: "=", value: newVal },
    ];
    search.value.vencida["exp_pre_ActuacionUsuarioDependencia.DependenciaID"] = [
      { operator: "=", value: newVal },
    ];

    fetchEstadisticas();
  });

  return {
    fetchEstadisticas,
    loading,
    dependenciaSelected,
    vencidas,
    enCurso,
    finalizadas,
    UFIStats,
    gralStats,
    ordenUFIs,
  };
});
